import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import api from '../services/api';

const SUBJECT_LABEL = { physics: 'Physics', chemistry: 'Chemistry', mathematics: 'Maths' };

export default function TeacherDetail() {
  const { teacherId } = useParams();
  const [teacher, setTeacher] = useState(null);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingSubjects, setEditingSubjects] = useState(false);
  const [draftSubjects, setDraftSubjects] = useState([]);

  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true);
        const [teacherResult, batchesResult] = await Promise.all([
          api.getTeacher(teacherId),
          api.getBatches({ teacher_id: teacherId, filter: 'all', limit: 50 }),
        ]);
        setTeacher(teacherResult.data);
        setBatches(batchesResult.data.batches || []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [teacherId]);

  const refreshData = async () => {
    try {
      setLoading(true);
      const [teacherResult, batchesResult] = await Promise.all([
        api.getTeacher(teacherId),
        api.getBatches({ teacher_id: teacherId, filter: 'all', limit: 50 }),
      ]);
      setTeacher(teacherResult.data);
      setBatches(batchesResult.data.batches || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  async function handleSaveSubjects() {
    try {
      await api.updateTeacherSubjects(teacherId, draftSubjects);
      setEditingSubjects(false);
      await refreshData();
    } catch (err) {
      setError(err.message);
    }
  }

  function startEditSubjects() {
    setDraftSubjects(teacher.subjects || []);
    setEditingSubjects(true);
  }

  const handleToggleActive = async () => {
    const action = teacher.is_active ? 'deactivate' : 'activate';
    if (!confirm(`Are you sure you want to ${action} this teacher?`)) {
      return;
    }
    try {
      if (teacher.is_active) {
        await api.deactivateTeacher(teacherId);
      } else {
        await api.updateTeacher(teacherId, { is_active: true });
      }
      await refreshData();
    } catch (err) {
      setError(err.message);
    }
  };

  const formatDate = (date) => {
    if (!date) return 'Never';
    try {
      return formatDistanceToNow(new Date(date), { addSuffix: true });
    } catch {
      return 'Unknown';
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          Loading teacher details...
        </div>
      </div>
    );
  }

  if (error || !teacher) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error || 'Teacher not found'}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Breadcrumb */}
      <nav className="mb-6">
        <Link to="/teachers" className="text-primary-600 hover:text-primary-800">
          ← Back to Teachers
        </Link>
      </nav>

      {/* Teacher Profile Card */}
      <div className="bg-white rounded-xl shadow-sm border border-teacher-border p-6 mb-6">
        <div className="flex justify-between items-start">
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-teacher-text-primary mb-2">
              {teacher.first_name} {teacher.last_name}
            </h1>
            <div className="space-y-1 text-sm text-teacher-text-secondary">
              <div>📧 {teacher.email}</div>
              <div>📱 {teacher.phone_number}</div>
              {teacher.coaching_institute_name && (
                <div>🏫 {teacher.coaching_institute_name}</div>
              )}
              {teacher.coaching_institute_location && (
                <div>📍 {teacher.coaching_institute_location}</div>
              )}
            </div>
          </div>
          <div className="flex flex-col items-end gap-3">
            <button
              onClick={handleToggleActive}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                teacher.is_active
                  ? 'bg-red-100 text-red-700 hover:bg-red-200'
                  : 'bg-green-100 text-green-700 hover:bg-green-200'
              }`}
            >
              {teacher.is_active ? 'Deactivate Teacher' : 'Activate Teacher'}
            </button>
            <div className="text-right">
              <div className="text-sm text-teacher-text-secondary mb-1">Total Students</div>
              <div className="text-3xl font-bold text-primary-600">
                {batches.reduce((sum, b) => sum + (b.total_students || 0), 0)}
              </div>
              <div className="text-xs text-teacher-text-tertiary mt-1">across {batches.length} batch{batches.length === 1 ? '' : 'es'}</div>
            </div>
          </div>
        </div>

        <div className="mt-6 pt-6 border-t border-teacher-border flex gap-4">
          <div className="flex-1">
            <div className="text-xs text-teacher-text-secondary mb-1">Status</div>
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
              teacher.is_active ? 'bg-green-100 text-green-700' : 'bg-teacher-bg-secondary text-teacher-text-primary'
            }`}>
              {teacher.is_active ? 'Active' : 'Inactive'}
            </span>
          </div>
          <div className="flex-1">
            <div className="text-xs text-teacher-text-secondary mb-1">Subjects</div>
            {!editingSubjects ? (
              <div className="flex items-center gap-2">
                <div className="flex flex-wrap gap-1">
                  {(teacher.subjects || []).map(s => (
                    <span key={s} className="text-xs bg-purple-50 text-purple-700 px-2 py-0.5 rounded">
                      {SUBJECT_LABEL[s] || s}
                    </span>
                  ))}
                  {(!teacher.subjects || teacher.subjects.length === 0) && (
                    <span className="text-xs text-teacher-text-tertiary">none</span>
                  )}
                </div>
                <button onClick={startEditSubjects}
                  className="text-xs text-primary-600 hover:text-primary-800">edit</button>
              </div>
            ) : (
              <div className="flex flex-wrap gap-1 items-center">
                {['physics', 'chemistry', 'mathematics'].map(s => {
                  const checked = draftSubjects.includes(s);
                  return (
                    <button key={s} type="button"
                      onClick={() => setDraftSubjects(checked
                        ? draftSubjects.filter(x => x !== s)
                        : [...draftSubjects, s])}
                      className={`text-xs px-2 py-0.5 rounded ${
                        checked ? 'bg-primary-600 text-white' : 'bg-teacher-bg-secondary text-teacher-text-secondary hover:bg-gray-200'
                      }`}>
                      {SUBJECT_LABEL[s]}
                    </button>
                  );
                })}
                <button onClick={handleSaveSubjects} disabled={draftSubjects.length === 0}
                  className="ml-2 text-xs text-green-700 hover:text-green-900 disabled:opacity-50">save</button>
                <button onClick={() => setEditingSubjects(false)}
                  className="text-xs text-teacher-text-secondary hover:text-teacher-text-primary">cancel</button>
              </div>
            )}
          </div>
          <div className="flex-1">
            <div className="text-xs text-teacher-text-secondary mb-1">Created</div>
            <div className="text-sm text-teacher-text-primary">{formatDate(teacher.created_at)}</div>
          </div>
          <div className="flex-1">
            <div className="text-xs text-teacher-text-secondary mb-1">Last Login</div>
            <div className="text-sm text-teacher-text-primary">{formatDate(teacher.last_login_at)}</div>
          </div>
        </div>
      </div>

      {/* Batches section */}
      <div className="bg-white rounded-xl shadow-sm border border-teacher-border mb-6 overflow-hidden">
        <div className="p-6 border-b border-teacher-border flex justify-between items-center">
          <div>
            <h2 className="text-lg font-semibold text-teacher-text-primary">Batches</h2>
            <p className="text-xs text-teacher-text-secondary mt-1">All batches this teacher is part of</p>
          </div>
          <Link to="/batches" className="text-sm text-primary-600 hover:text-primary-800">
            Add to a batch in Batches page →
          </Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-teacher-bg border-b border-teacher-border">
                <th className="px-6 py-3 text-left text-xs font-medium text-teacher-text-secondary uppercase">Batch</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-teacher-text-secondary uppercase">Classes</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-teacher-text-secondary uppercase">Students</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-teacher-text-secondary uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-teacher-text-secondary uppercase">View as Teacher</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {batches.length === 0 ? (
                <tr><td colSpan="5" className="px-6 py-8 text-center text-teacher-text-secondary">Not in any batches yet</td></tr>
              ) : (
                batches.map(b => (
                  <tr key={b.id} className="hover:bg-teacher-bg">
                    <td className="px-6 py-3 text-sm font-medium">
                      <Link to={`/batches/${b.id}`} className="text-primary-600 hover:underline">{b.name}</Link>
                    </td>
                    <td className="px-6 py-3 text-sm text-teacher-text-secondary">{(b.classes || []).join(', ') || '—'}</td>
                    <td className="px-6 py-3 text-sm text-teacher-text-secondary">{b.total_students || 0}</td>
                    <td className="px-6 py-3 text-sm">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        b.is_active !== false ? 'bg-green-100 text-green-700' : 'bg-teacher-bg-secondary text-teacher-text-secondary'
                      }`}>{b.is_active !== false ? 'Active' : 'Archived'}</span>
                    </td>
                    <td className="px-6 py-3 text-sm">
                      <div className="flex flex-wrap gap-1">
                        {(teacher.subjects || []).map(s => (
                          <Link key={s}
                            to={`/teachers/${teacher.id}/batches/${b.id}/${s}`}
                            className="text-xs bg-purple-100 text-purple-700 hover:bg-purple-200 px-2 py-1 rounded">
                            {SUBJECT_LABEL[s]} →
                          </Link>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Students are now managed per-batch from BatchDetail. The Batches
          section above lists every batch this teacher is in; click into any
          batch to see/manage its students. */}
    </div>
  );
}
